import { DateTime } from 'luxon';

import { ReferenceNumberType } from '@rygen/rygen-ui/model/ReferenceNumberType';

import { ReferenceNumberSource } from './ReferenceNumberSource';

export class ReferenceNumber {
  id?: number;
  typeId?: number;
  typeNaturalId?: string;
  typeName?: string;
  value?: string;
  source?: ReferenceNumberSource;
  levels?: string[] = [];

  constructor(opts?: Partial<ReferenceNumber>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.typeId != null) {
      this.typeId = opts.typeId;
    }
    if (opts?.typeNaturalId != null) {
      this.typeNaturalId = opts.typeNaturalId;
    }
    if (opts?.typeName != null) {
      this.typeName = opts.typeName;
    }
    if (opts?.value != null) {
      this.value = opts.value;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.levels != null) {
      this.levels = opts.levels;
    }
  }

  get type(): ReferenceNumberType | undefined {
    return this.typeNaturalId
      ? new ReferenceNumberType({
          id: this.typeId,
          naturalId: this.typeNaturalId,
          name: this.typeName,
        })
      : undefined;
  }

  set type(type: ReferenceNumberType | undefined) {
    this.typeId = type?.id;
    this.typeName = type?.name;
    this.typeNaturalId = type?.naturalId;
  }

  get valueAsNumber(): number | undefined {
    if (this.value === undefined) {
      return undefined;
    }

    try {
      return Number.parseFloat(this.value);
    } catch {
      return undefined;
    }
  }

  set valueAsNumber(value: number | undefined) {
    this.value = value?.toString();
  }

  get valueAsArray(): string[] | undefined {
    if (this.value === undefined) {
      return undefined;
    }

    return this.value.split(',').map((s) => s.trim());
  }

  set valueAsArray(value: string[] | undefined) {
    this.value = value?.join(', ');
  }

  get valueAsDate(): Date | undefined {
    if (this.value === undefined) {
      return undefined;
    }

    try {
      const valueWithoutOffset = this.value.replace(/[+-]\d{2}:\d{2}$/, '');
      return new Date(valueWithoutOffset);
    } catch {
      return undefined;
    }
  }

  set valueAsDate(value: Date | undefined) {
    const valueWithoutOffset = value
      ? DateTime.fromJSDate(value).toISO({ includeOffset: false })
      : undefined;
    // DateTime.toISO() can return `null` so we normalize that to `undefined`
    this.value = valueWithoutOffset ?? undefined;
  }

  get valueAsDateTime(): DateTime<boolean> | undefined {
    if (this.value === undefined) {
      return undefined;
    }

    try {
      const valueWithoutOffset = this.value.replace(/[+-]\d{2}:\d{2}$/, '');
      return DateTime.fromISO(valueWithoutOffset);
    } catch {
      return undefined;
    }
  }

  set valueAsDateTime(value: DateTime<boolean> | undefined) {
    // DateTime.toISO() can return `null` so we normalize that to `undefined`
    this.value = value?.toISO({ includeOffset: false }) ?? undefined;
  }
}
